import { observable, toJS, flow, action } from "mobx";
import {
	getCampaigns,
	createCampaign,
	renameCampaign,
	deleteCampaigns
} from "services/campaigns";
import { registerHandler, delay, getOptions } from "services/api";
import { message } from "antd";
import { encrypt } from "common/abc";

export default class CampaignsStore {
	constructor() {
		this.campaigns = observable.array([]);
		this.strategies = observable.array([]);
		this.options = observable.object({});
		this.ui = this.getUI();
	}

	getUI = () => {
		let ui = {
			loadingCampaigns: false,
			addCampaignDialogVisible: false,
			addEditCampaignDialogVisible: false,
			duplicateCampaignDialogVisible: false,
			duplicatedCampaign: null,
			editedCampaign: null, //When editedCampaign is not null, it means that we are editing that campaign
			renamingCampaign: false,
			deletingCampaigns: false,
			creatingCampaign: false
		};
		return observable.object(ui);
	};

	@action
	showAddCampaignDialog = () => {
		this.ui.addCampaignDialogVisible = true;
		this.ui.editedCampaign = null;
	};

	@action
	hideAddCampaignDialog = () => {
		this.ui.addCampaignDialogVisible = false;
		this.ui.editedCampaign = null;
	};

	@action
	showDuplicateCampaignDialog = campaign => {
		this.ui.duplicateCampaignDialogVisible = true;
		this.ui.duplicatedCampaign = campaign;
	};

	@action
	hideDuplicateCampaignDialog = () => {
		this.ui.duplicateCampaignDialogVisible = false;
		this.ui.duplicatedCampaign = null;
	};

	@action
	showEditCampaignDialog = campaign => {
		this.ui.addEditCampaignDialogVisible = true;
		this.ui.editedCampaign = campaign;
	};

	@action
	hideAddEditCampaignDialog = () => {
		this.ui.addEditCampaignDialogVisible = false;
		this.ui.editedCampaign = null;
	};

	createCampaign = flow(function*(name) {
		this.ui.creatingCampaign = true;
		
		const response = yield createCampaign(encrypt(JSON.stringify(name)));
		this.ui.creatingCampaign = false;

		if (response == null) {
			message.error("Failed to create new campaign. Unknown error", 3);
			return;
		}

		if (response.error) {
			message.error(response.error.message, 3);
			return;
		}

		message.success("Successfully created a new campaign", 3);
		this.ui.addCampaignDialogVisible = false;
		this.ui.editedCampaign = null;
	});

	remoteRenameCampaign = flow(function*(campaign) {
		this.ui.renamingCampaign = true;
		const response = yield renameCampaign(campaign);
		this.ui.renamingCampaign = false;
		if (response == null) {
			message.error("Failed to rename the campaign. Unknown error", 3);
			return;
		}
		if (response.error) {
			message.error(response.error.message, 3);
			return;
		}

		message.success("Successfully rename the campaign", 3);
		this.ui.addEditCampaignDialogVisible = false;
		this.ui.editedCampaign = null;
	});

	@action
	renameCampaign = campaign => {
		const foundIndex = this.campaigns.findIndex(el => {
			return el.id == campaign.id;
		});

		if (foundIndex != -1) {
			this.campaigns[foundIndex].name = campaign.name;
		}
	};

	remoteDeleteCampaigns = flow(function*(ids) {
		this.ui.deletingCampaigns = true;
		const response = yield deleteCampaigns(ids);
		this.ui.deletingCampaigns = false;
		if (response == null) {
			message.error("Failed to delete selected campaigns. Unknown error", 3);
			return;
		}
		if (response.error) {
			message.error(response.error.message, 3);
			return;
		}

		message.success("Successfully deleted selected campaigns", 3);
	});

	@action
	deleteCampaigns = ids => {
		this.campaigns = observable.array(
			this.campaigns.filter(campaign => !ids.includes(campaign.id))
		);
	};

	@action
	addCampaign = campaign => {
		this.campaigns.push(campaign);
	};

	@action
	changeCampaignStatus = campaign => {
		const foundIndex = this.campaigns.findIndex(el => {
			return el.id == campaign.id;
		});

		if (foundIndex != -1) {
			this.campaigns[foundIndex] = campaign;
		}
	};

	loadCampaigns = flow(function*() {
		this.ui = this.getUI();
		this.ui.loadingCampaigns = true;
		const [response, optionsResponse] = yield Promise.all(
			yield [
				getCampaigns(),
				getOptions()
			]
		)						

		this.ui.loadingCampaigns = false;
		if (response == null) {
			message.error("Failed to load campaigns. Unexpected error", 3);
			return;
		}
		if (response.error) {
			message.error(response.error.message, 3);
			return;
		}

		this.options = observable.object(optionsResponse.options);

		this.campaigns = observable.array(response.campaigns);
		this.strategies = observable.array(response.strategies);

		registerHandler([
			{
				path: "campaigns",
				handler: (err, msg) => {
					if (err != null) return;
					// console.log(msg);
					const obj = JSON.parse(msg.body);
					if (obj.action == "addNew") {
						var campaign = obj.campaign;
						this.addCampaign(campaign);
						return;
					}

					if (obj.action == "rename") {						
						this.renameCampaign({
							name: obj.name,
							id: obj.id
						});
						return;
					}

					if (obj.action == "campaignsDeleted") {
						this.deleteCampaigns(obj.ids);
						return;
					}

					if (obj.action == "statusChanged") {
						this.changeCampaignStatus(obj.campaign);
						return;
					}
				}
			}
		]);
	});
}
